import React, { useState, useCallback, useEffect, useMemo } from "react";
import { Modal, Input, Table, message, Typography, DatePicker, Button, Row, Col, Tag, Divider } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import apiClient from "../../../../api/axios";

dayjs.extend(utc);

const { Text } = Typography;
const { RangePicker } = DatePicker;

const EXCLUDED_STATUSES = new Set(["REPLACED", "PLAN_CANCELLED", "CANCELLED"]);

const fmtTimeDiff = (seconds) => {
    if (seconds == null) return "-";
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
};

const STATUS_COLOR = { COMPLETED: "green", SCHEDULED: "blue", DEPARTED: "orange", DIVERTED: "red" };

const ManualLinkModal = ({ open, record, onClose, onConfirm }) => {
    const [flightNumber, setFlightNumber] = useState("");
    const [dateRange, setDateRange] = useState(null);
    const [departureAirport, setDepartureAirport] = useState("");
    const [arrivalAirport, setArrivalAirport] = useState("");
    const [aircraftReg, setAircraftReg] = useState("");
    const [loading, setLoading] = useState(false);
    const [searchResults, setSearchResults] = useState(null);
    const [selectedId, setSelectedId] = useState(null);
    const [confirming, setConfirming] = useState(false);

    const candidates = useMemo(() => {
        const raw = record?.match_candidates?.candidates;
        if (!raw?.length) return [];
        return [...raw]
            .sort((a, b) => (a.rank ?? 0) - (b.rank ?? 0))
            .map((c) => ({ ...c, id: c.flight_operation_id, _source: "candidate" }));
    }, [record?.match_candidates?.candidates]);

    const hasCandidates = candidates.length > 0;

    useEffect(() => {
        if (open && record) {
            const fn = record.flight_number_norm || record.flight_number_raw || "";
            setFlightNumber(fn);
            if (record.flight_date) {
                const d = dayjs.utc(record.flight_date);
                if (d.isValid()) {
                    setDateRange([d.subtract(1, "day"), d.add(1, "day")]);
                }
            } else {
                setDateRange(null);
            }
            setDepartureAirport(record.settlement_departure_airport || "");
            setArrivalAirport(record.settlement_arrival_airport || "");
            setAircraftReg(record.settlement_aircraft_reg || "");
            setSelectedId(null);
            setSearchResults(null);
        }
    }, [open, record]);

    const handleSearch = useCallback(async () => {
        const fn = flightNumber.trim();
        const dep = departureAirport.trim().toUpperCase();
        const arr = arrivalAirport.trim().toUpperCase();
        const reg = aircraftReg.trim().toUpperCase();
        if (!fn && !dateRange && !dep && !arr && !reg) {
            message.warning("请至少填写一项搜索条件");
            return;
        }
        setLoading(true);
        try {
            const params = { page_size: 50 };
            if (fn) params.flight_number = fn;
            if (dateRange?.[0]) params.start_date = dateRange[0].format("YYYY-MM-DD");
            if (dateRange?.[1]) params.end_date = dateRange[1].format("YYYY-MM-DD");
            if (dep) params.departure_airport = dep;
            if (arr) params.arrival_airport = arr;
            if (reg) params.aircraft_reg = reg;

            const res = await apiClient.get("/v1/flight-operations/search", { params });
            const raw = res.data?.items || res.data?.records || res.data || [];
            const data = (Array.isArray(raw) ? raw : []).filter(
                (f) => !EXCLUDED_STATUSES.has(f.status)
            );
            setSearchResults(data);
            setSelectedId(null);
            if (!data.length) message.info("未找到匹配航班，试试放宽条件");
        } catch (err) {
            message.error(`搜索失败: ${err.message}`);
        } finally {
            setLoading(false);
        }
    }, [flightNumber, dateRange, departureAirport, arrivalAirport, aircraftReg]);

    const showingSearch = searchResults !== null;
    const dataSource = showingSearch ? searchResults : candidates;

    const columns = useMemo(() => {
        const cols = [
            { title: "航班号", dataIndex: "flight_number", width: 100 },
            {
                title: "日期",
                dataIndex: "flight_date",
                width: 110,
                render: (v) => v ? dayjs.utc(v).format("YYYY-MM-DD") : "-",
            },
            { title: "始发", dataIndex: "departure_airport", width: 60 },
            { title: "目的", dataIndex: "arrival_airport", width: 60 },
            {
                title: "STD (UTC)",
                dataIndex: "std",
                width: 120,
                render: (v) => v ? dayjs.utc(v).format("MM-DD HH:mm") : "-",
            },
            {
                title: "ATD (UTC)",
                dataIndex: "atd",
                width: 120,
                render: (v) => v ? dayjs.utc(v).format("MM-DD HH:mm") : "-",
            },
            { title: "飞机", dataIndex: "aircraft_reg", width: 90 },
            {
                title: "状态",
                dataIndex: "status",
                width: 90,
                render: (v) => <Tag color={STATUS_COLOR[v] || "default"}>{v || "-"}</Tag>,
            },
        ];
        if (!showingSearch && hasCandidates) {
            cols.splice(0, 0, {
                title: "#",
                dataIndex: "rank",
                width: 45,
                render: (v) => <Tag>{v}</Tag>,
            });
            cols.push({
                title: "时间差",
                dataIndex: "time_diff_seconds",
                width: 80,
                render: (v) => fmtTimeDiff(v),
            });
        }
        return cols;
    }, [showingSearch, hasCandidates]);

    const handleOk = async () => {
        if (!selectedId || !record || confirming) return;
        setConfirming(true);
        try {
            await onConfirm(record, selectedId);
        } finally {
            setConfirming(false);
        }
    };

    const handleCancel = () => {
        setSelectedId(null);
        setSearchResults(null);
        setFlightNumber("");
        setDateRange(null);
        setDepartureAirport("");
        setArrivalAirport("");
        setAircraftReg("");
        onClose();
    };

    const switchToCandidates = () => {
        setSearchResults(null);
        setSelectedId(null);
    };

    return (
        <Modal
            title={`关联航班 — ${record?.flight_number_norm || record?.flight_number_raw || ""}`}
            open={open}
            onCancel={handleCancel}
            onOk={handleOk}
            okButtonProps={{ disabled: !selectedId, loading: confirming }}
            okText="确认关联"
            width={1000}
            destroyOnHidden
        >
            {record && (
                <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
                    飞机: {record.aircraft_msn || "-"}
                    {record.settlement_aircraft_reg ? ` (${record.settlement_aircraft_reg})` : ""}
                    {" | "}日期: {record.flight_date || "-"}
                    {" | "}航线: {record.settlement_departure_airport || "?"}-{record.settlement_arrival_airport || "?"}
                    {record.settlement_atd && (
                        <> | Out: {dayjs.utc(record.settlement_atd).format("MM-DD HH:mm")} UTC</>
                    )}
                    {record.settlement_atd_icao && (
                        <> | Off: {dayjs.utc(record.settlement_atd_icao).format("MM-DD HH:mm")} UTC</>
                    )}
                    {record.settlement_ata_icao && (
                        <> | On: {dayjs.utc(record.settlement_ata_icao).format("MM-DD HH:mm")} UTC</>
                    )}
                    {record.settlement_ata && (
                        <> | In: {dayjs.utc(record.settlement_ata).format("MM-DD HH:mm")} UTC</>
                    )}
                    {record.match_status && <> | 状态: {record.match_status}</>}
                </Text>
            )}

            <Row gutter={[8, 8]} style={{ marginBottom: 12 }} align="middle">
                <Col>
                    <Input
                        placeholder="航班号"
                        value={flightNumber}
                        onChange={(e) => setFlightNumber(e.target.value)}
                        onPressEnter={handleSearch}
                        style={{ width: 130 }}
                        allowClear
                    />
                </Col>
                <Col>
                    <Input
                        placeholder="始发站(IATA)"
                        value={departureAirport}
                        onChange={(e) => setDepartureAirport(e.target.value.toUpperCase())}
                        onPressEnter={handleSearch}
                        style={{ width: 120 }}
                        maxLength={4}
                        allowClear
                    />
                </Col>
                <Col>
                    <Input
                        placeholder="目的站(IATA)"
                        value={arrivalAirport}
                        onChange={(e) => setArrivalAirport(e.target.value.toUpperCase())}
                        onPressEnter={handleSearch}
                        style={{ width: 120 }}
                        maxLength={4}
                        allowClear
                    />
                </Col>
                <Col>
                    <Input
                        placeholder="飞机号"
                        value={aircraftReg}
                        onChange={(e) => setAircraftReg(e.target.value.toUpperCase())}
                        onPressEnter={handleSearch}
                        style={{ width: 140 }}
                        allowClear
                    />
                </Col>
                <Col>
                    <RangePicker
                        value={dateRange}
                        onChange={setDateRange}
                        format="YYYY-MM-DD"
                        style={{ width: 240 }}
                        allowClear
                    />
                </Col>
                <Col>
                    <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch} loading={loading}>
                        查询
                    </Button>
                </Col>
                {showingSearch && hasCandidates && (
                    <Col>
                        <Button type="link" onClick={switchToCandidates}>
                            返回候选列表 ({candidates.length})
                        </Button>
                    </Col>
                )}
            </Row>

            {!showingSearch && hasCandidates && (
                <Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
                    系统推荐 {candidates.length} 个候选，也可通过上方搜索查找其他航班
                </Text>
            )}
            {showingSearch && (
                <Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
                    搜索结果 {searchResults.length} 条
                </Text>
            )}

            <Table
                size="small"
                columns={columns}
                dataSource={dataSource}
                rowKey="id"
                loading={loading}
                pagination={false}
                scroll={{ y: 300 }}
                rowSelection={{
                    type: "radio",
                    selectedRowKeys: selectedId ? [selectedId] : [],
                    onChange: (keys) => setSelectedId(keys[0] || null),
                }}
                onRow={(row) => ({
                    onClick: () => setSelectedId(row.id),
                    style: { cursor: "pointer" },
                })}
            />
        </Modal>
    );
};

export default ManualLinkModal;
